import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Camera, CameraOff, ReceiptText, ArrowLeft } from "lucide-react";
import jsQR from "jsqr";
import { ApiService } from "../../services/api";
import { isYesToolsHostname } from "../../utils/yesTools";

interface KioskOrderItem {
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface KioskOrder {
  code: string;
  status: "awaiting_payment" | "paid" | "cancelled" | "expired" | string;
  totalAmount: number;
  currency?: string;
  items: KioskOrderItem[];
  createdAt?: string | null;
  expiresAt?: string | null;
}

const KIOSK_QR_RE = /^YESKIOSK:([A-Z0-9]{6})$/i;
const CODE_RE = /^[A-Z0-9]{6}$/;

export const formatKioskPrice = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
  return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 }).format(Number(value));
};

const statusMessage = (status: string) => {
  if (status === "paid") return "Bu sipariş zaten ödenmiş.";
  if (status === "cancelled") return "Sipariş kioskta iptal edilmiş.";
  if (status === "expired") return "Sipariş süresi dolmuş. Lütfen kioskta yeni sipariş oluşturun.";
  return null;
};

export default function KioskPayPage() {
  const isYesToolsDomain =
    typeof window !== "undefined" && isYesToolsHostname(window.location.hostname);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [order, setOrder] = useState<KioskOrder | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [manualCode, setManualCode] = useState("");
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef(0);
  const lastScanRef = useRef(0);
  const loadingRef = useRef(false);

  const loadOrder = useCallback(async (code: string) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoadingOrder(true);
    setOrderError(null);
    setPayError(null);
    try {
      const res = await ApiService.getApi(`kiosk/orders/${code}`);
      setOrder(res?.data || null);
    } catch (e: any) {
      setOrder(null);
      setOrderError(e?.message || "Sipariş alınamadı.");
    } finally {
      setLoadingOrder(false);
      loadingRef.current = false;
    }
  }, []);

  // ?code= ile gelinirse (ör. başarısız ödeme sonrası tekrar dene) direkt yükle;
  // loadOrder tekrar girişe karşı zaten korumalı, deps değişimi sorun yaratmaz
  useEffect(() => {
    const initial = String(searchParams.get("code") || "").toUpperCase();
    if (CODE_RE.test(initial)) loadOrder(initial);
  }, [searchParams, loadOrder]);

  const stopCamera = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  // Kamera: sipariş ekranı yokken açık; QR bulununca durdurulur
  useEffect(() => {
    if (order || loadingOrder) return;

    let cancelled = false;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    const tick = () => {
      if (cancelled) return;
      const video = videoRef.current;
      // ~4 tarama/sn yeterli; sürekli tam kare analizi pili yer
      if (video && video.readyState >= 2 && ctx && Date.now() - lastScanRef.current > 250) {
        lastScanRef.current = Date.now();
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const found = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" });
        const match = found?.data ? KIOSK_QR_RE.exec(found.data.trim()) : null;
        if (match) {
          stopCamera();
          loadOrder(match[1].toUpperCase());
          return;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setCameraError(null);
        rafRef.current = requestAnimationFrame(tick);
      } catch {
        setCameraError("Kameraya erişilemedi. Kodu aşağıya elle girebilirsiniz.");
      }
    })();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [order, loadingOrder, loadOrder, stopCamera]);

  // Geri sayım: expiresAt'e göre kalan süre
  useEffect(() => {
    if (!order?.expiresAt || order.status !== "awaiting_payment") {
      setRemainingSeconds(null);
      return;
    }
    const compute = () =>
      setRemainingSeconds(Math.max(0, Math.floor((new Date(order.expiresAt!).getTime() - Date.now()) / 1000)));
    compute();
    const interval = setInterval(compute, 1000);
    return () => clearInterval(interval);
  }, [order]);

  const handleManualSubmit = () => {
    const code = manualCode.trim().toUpperCase();
    if (!CODE_RE.test(code)) {
      setOrderError("Kod 6 haneli olmalı (harf ve rakam).");
      return;
    }
    loadOrder(code);
  };

  const handlePay = async () => {
    if (!order) return;
    setPaying(true);
    setPayError(null);
    try {
      const res = await ApiService.postApi(`kiosk/orders/${order.code}/checkout`, {});
      const url = res?.data?.paymentPageUrl;
      if (!url) throw new Error("Ödeme sayfası alınamadı.");
      window.location.assign(url);
    } catch (e: any) {
      setPayError(e?.message || "Ödeme başlatılamadı.");
      setPaying(false);
    }
  };

  const resetToScan = () => {
    setOrder(null);
    setOrderError(null);
    setPayError(null);
    setManualCode("");
  };

  if (!isYesToolsDomain) {
    return <Navigate to="/dashboard" replace />;
  }

  const expiredNow = remainingSeconds !== null && remainingSeconds <= 0;
  const blockedMessage = order ? statusMessage(order.status) : null;

  return (
    <div className="flex flex-col gap-5 p-4 md:p-6 max-w-md mx-auto w-full">
      <div className="flex items-center gap-3">
        <button
          onClick={() => (order ? resetToScan() : navigate("/cafe-menu"))}
          className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          aria-label="Geri"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-white">Kiosk Ödeme</h1>
          <p className="text-sm text-white/60 mt-0.5">
            {order ? "Siparişinizi kontrol edip ödemeyi tamamlayın." : "Kiosk ekranındaki QR kodu okutun."}
          </p>
        </div>
      </div>

      {!order ? (
        <div className="flex flex-col gap-4">
          <div className="bg-black rounded-xl overflow-hidden relative aspect-square">
            {cameraError ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/70 p-6 text-center">
                <CameraOff className="w-10 h-10" />
                <p className="text-sm">{cameraError}</p>
              </div>
            ) : (
              <>
                <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                  <div className="w-56 h-56 border-2 border-white/70 rounded-2xl" />
                </div>
                {loadingOrder && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-white" />
                  </div>
                )}
              </>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
              Ya da kodu elle girin
            </p>
            <div className="flex gap-2">
              <input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
                placeholder="ABC123"
                className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2.5 text-center text-lg font-mono tracking-[0.3em] uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
                inputMode="text"
                autoCapitalize="characters"
                autoCorrect="off"
              />
              <button
                onClick={handleManualSubmit}
                disabled={loadingOrder || manualCode.length !== 6}
                className="px-4 py-2.5 rounded-lg bg-blue-700 text-white text-sm font-semibold hover:bg-blue-800 transition-colors disabled:opacity-50"
              >
                {loadingOrder ? <Loader2 className="w-4 h-4 animate-spin" /> : "Getir"}
              </button>
            </div>
            {orderError && <p className="text-xs text-red-600 mt-2">{orderError}</p>}
            {!cameraError && (
              <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                <Camera className="w-3.5 h-3.5" /> Kamera kiosk ekranındaki QR'ı otomatik tanır.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-700">
                <ReceiptText className="w-4 h-4" />
                <span className="text-sm font-semibold">Sipariş {order.code}</span>
              </div>
              {order.status === "awaiting_payment" && remainingSeconds !== null && !expiredNow && (
                <span className={`text-xs font-semibold tabular-nums ${remainingSeconds < 120 ? "text-red-600" : "text-gray-400"}`}>
                  {String(Math.floor(remainingSeconds / 60)).padStart(2, "0")}:{String(remainingSeconds % 60).padStart(2, "0")}
                </span>
              )}
            </div>

            <div className="divide-y divide-gray-50">
              {(order.items || []).map((item, i) => (
                <div key={i} className="px-4 py-3 flex items-center gap-3">
                  <span className="text-sm text-gray-800 flex-1 min-w-0">{item.name}</span>
                  <span className="text-xs text-gray-400 shrink-0 tabular-nums">
                    {item.quantity} × {formatKioskPrice(item.unitPrice)}
                  </span>
                  <span className="text-sm font-semibold text-gray-800 shrink-0 tabular-nums w-20 text-right">
                    {formatKioskPrice(item.total)}
                  </span>
                </div>
              ))}
            </div>

            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <span className="text-sm font-bold text-gray-700">Toplam</span>
              <span className="text-lg font-extrabold text-gray-900 tabular-nums">
                {formatKioskPrice(order.totalAmount)}
              </span>
            </div>
          </div>

          {blockedMessage ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              {blockedMessage}
            </div>
          ) : expiredNow ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              Sipariş süresi doldu. Lütfen kioskta yeni sipariş oluşturun.
            </div>
          ) : (
            <button
              onClick={handlePay}
              disabled={paying}
              className="w-full py-3.5 rounded-xl bg-blue-700 text-white text-base font-bold hover:bg-blue-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {paying ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Ödeme sayfasına yönlendiriliyor...
                </>
              ) : (
                <>Ödemeyi Yap · {formatKioskPrice(order.totalAmount)}</>
              )}
            </button>
          )}
          {payError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{payError}</div>
          )}
        </div>
      )}
    </div>
  );
}
